/**
 * CustomFieldsAdmin — admin UI section in Board Settings for managing
 * custom field definitions: list existing fields, add/edit/delete fields,
 * and toggle showOnCard visibility.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.7
 */

import React, { useCallback, useState } from 'react';
import * as api from '../../services/api-client';
import { useActiveBoard, CustomFieldDefinition, FieldType } from '../../state/active-board-context';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Toggle } from '../ui/toggle';
import { Badge } from '../ui/badge';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'boolean', label: 'Boolean' },
  { value: 'dropdown-single', label: 'Dropdown (Single)' },
  { value: 'dropdown-multi', label: 'Dropdown (Multi)' },
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'linked-to-list', label: 'Linked to List' },
];

const MAX_NAME_LENGTH = 50;
const MAX_OPTIONS = 20;
const MAX_OPTION_LENGTH = 50;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CustomFieldsAdminProps {
  readonly boardId: string;
}

interface FieldFormState {
  name: string;
  fieldType: FieldType;
  options: string[];
  showOnCard: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isDropdownType(type: FieldType): boolean {
  return type === 'dropdown-single' || type === 'dropdown-multi';
}

function getTypeBadgeVariant(type: FieldType): 'default' | 'secondary' | 'outline' {
  switch (type) {
    case 'boolean':
      return 'outline';
    case 'text':
      return 'secondary';
    default:
      return 'default';
  }
}

const emptyFormState: FieldFormState = {
  name: '',
  fieldType: 'text',
  options: [],
  showOnCard: false,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomFieldsAdmin({ boardId }: CustomFieldsAdminProps): React.ReactElement {
  const { state, fetchBoard } = useActiveBoard();
  const { customFieldDefinitions, accessLevel } = state;

  // Only admins can see and manage custom fields (Requirement 1.7)
  if (accessLevel !== 'admin') {
    return <></>;
  }

  return (
    <section className="mb-6" aria-labelledby="custom-fields-heading">
      <div className="flex items-center justify-between mb-2">
        <h3 id="custom-fields-heading" className="text-xs font-medium text-muted-foreground uppercase">
          Custom Fields
        </h3>
      </div>
      <CustomFieldsList
        boardId={boardId}
        definitions={customFieldDefinitions}
        onRefresh={() => fetchBoard(boardId)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Fields List
// ---------------------------------------------------------------------------

interface CustomFieldsListProps {
  readonly boardId: string;
  readonly definitions: readonly CustomFieldDefinition[];
  readonly onRefresh: () => void;
}

function CustomFieldsList({ boardId, definitions, onRefresh }: CustomFieldsListProps): React.ReactElement {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [deletingField, setDeletingField] = useState<CustomFieldDefinition | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!deletingField) return;
    setIsDeleting(true);
    try {
      await api.del(`/v1/boards/${boardId}/custom-fields/${deletingField.fieldId}`);
      onRefresh();
    } catch {
      // Delete failed — silently handle
    } finally {
      setIsDeleting(false);
      setDeletingField(null);
    }
  }, [boardId, deletingField, onRefresh]);

  const handleShowOnCardToggle = useCallback(async (field: CustomFieldDefinition) => {
    try {
      await api.put(`/v1/boards/${boardId}/custom-fields/${field.fieldId}`, {
        showOnCard: !field.showOnCard,
      });
      onRefresh();
    } catch {
      // Toggle failed — silently handle
    }
  }, [boardId, onRefresh]);

  return (
    <>
      {/* Existing fields */}
      {definitions.length === 0 && !showAddForm && (
        <p className="text-xs text-muted-foreground mb-2">No custom fields defined yet.</p>
      )}

      <div className="space-y-2 mb-3">
        {definitions.map((field) =>
          editingFieldId === field.fieldId ? (
            <FieldForm
              key={field.fieldId}
              boardId={boardId}
              existingField={field}
              onDone={() => { setEditingFieldId(null); onRefresh(); }}
              onCancel={() => setEditingFieldId(null)}
            />
          ) : (
            <FieldRow
              key={field.fieldId}
              field={field}
              onEdit={() => setEditingFieldId(field.fieldId)}
              onDelete={() => setDeletingField(field)}
              onToggleShowOnCard={() => handleShowOnCardToggle(field)}
            />
          ),
        )}
      </div>

      {/* Add Field button / form */}
      {showAddForm ? (
        <FieldForm
          boardId={boardId}
          onDone={() => { setShowAddForm(false); onRefresh(); }}
          onCancel={() => setShowAddForm(false)}
        />
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
          aria-label="Add custom field"
        >
          + Add Field
        </button>
      )}

      {/* Delete confirmation dialog (Requirement 1.5) */}
      <AlertDialog open={!!deletingField} onOpenChange={(open) => { if (!open) setDeletingField(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Field</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deletingField?.name}&rdquo;? Existing card values for this field will no longer be displayed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Field Row
// ---------------------------------------------------------------------------

interface FieldRowProps {
  readonly field: CustomFieldDefinition;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly onToggleShowOnCard: () => void;
}

function FieldRow({ field, onEdit, onDelete, onToggleShowOnCard }: FieldRowProps): React.ReactElement {
  return (
    <div className="flex items-center gap-2 p-2 border border-border rounded-md bg-muted/20">
      <div className="flex-1 min-w-0">
        <span className="text-sm text-card-foreground block truncate">{field.name}</span>
        <Badge variant={getTypeBadgeVariant(field.fieldType)} className="mt-0.5 text-[10px]">
          {field.fieldType}
        </Badge>
      </div>

      {/* Show on card toggle */}
      <Toggle
        size="sm"
        pressed={field.showOnCard}
        onPressedChange={onToggleShowOnCard}
        aria-label={`Show ${field.name} on card: ${field.showOnCard ? 'on' : 'off'}`}
        className="h-7 px-2 text-[10px]"
      >
        {field.showOnCard ? '👁' : '👁‍🗨'}
      </Toggle>

      {/* Edit */}
      <button
        onClick={onEdit}
        className="text-xs text-muted-foreground hover:text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded px-1"
        aria-label={`Edit field: ${field.name}`}
      >
        Edit
      </button>

      {/* Delete */}
      <button
        onClick={onDelete}
        className="text-xs text-destructive hover:text-destructive/80 focus:outline-none focus:ring-2 focus:ring-ring rounded px-1"
        aria-label={`Delete field: ${field.name}`}
      >
        Delete
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field Form (Add / Edit)
// ---------------------------------------------------------------------------

interface FieldFormProps {
  readonly boardId: string;
  readonly existingField?: CustomFieldDefinition;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

function FieldForm({ boardId, existingField, onDone, onCancel }: FieldFormProps): React.ReactElement {
  const [form, setForm] = useState<FieldFormState>(() => {
    if (existingField) {
      return {
        name: existingField.name,
        fieldType: existingField.fieldType,
        options: [...existingField.options],
        showOnCard: existingField.showOnCard,
      };
    }
    return { ...emptyFormState };
  });
  const [newOption, setNewOption] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!existingField;
  const showOptions = isDropdownType(form.fieldType);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, name: e.target.value.slice(0, MAX_NAME_LENGTH) }));
  };

  const handleTypeChange = (value: string) => {
    const fieldType = value as FieldType;
    setForm((prev) => ({
      ...prev,
      fieldType,
      // Clear options when switching away from dropdown types
      options: isDropdownType(fieldType) ? prev.options : [],
    }));
  };

  const handleAddOption = () => {
    const trimmed = newOption.trim().slice(0, MAX_OPTION_LENGTH);
    if (!trimmed) return;
    if (form.options.length >= MAX_OPTIONS) {
      setError(`Maximum ${MAX_OPTIONS} options allowed.`);
      return;
    }
    if (form.options.includes(trimmed)) {
      setError('Duplicate option.');
      return;
    }
    setForm((prev) => ({ ...prev, options: [...prev.options, trimmed] }));
    setNewOption('');
    setError(null);
  };

  const handleRemoveOption = (index: number) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleMoveOption = (index: number, direction: 'up' | 'down') => {
    setForm((prev) => {
      const newOptions = [...prev.options];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newOptions.length) return prev;
      [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
      return { ...prev, options: newOptions };
    });
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const validate = (): string | null => {
    const trimmedName = form.name.trim();
    if (!trimmedName) return 'Field name is required.';
    if (trimmedName.length > MAX_NAME_LENGTH) return `Field name must be ${MAX_NAME_LENGTH} characters or less.`;
    if (showOptions && form.options.length === 0) return 'At least one option is required for dropdown types.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (isEditing) {
        // PUT update
        const payload: Record<string, unknown> = { name: form.name.trim() };
        // Include fieldType if it changed from the original
        if (form.fieldType !== existingField.fieldType) {
          payload.fieldType = form.fieldType;
        }
        if (isDropdownType(form.fieldType)) {
          payload.options = form.options;
        }
        payload.showOnCard = form.showOnCard;
        await api.put(`/v1/boards/${boardId}/custom-fields/${existingField.fieldId}`, payload);
      } else {
        // POST create
        const payload: Record<string, unknown> = {
          name: form.name.trim(),
          fieldType: form.fieldType,
          showOnCard: form.showOnCard,
        };
        if (isDropdownType(form.fieldType)) {
          payload.options = form.options;
        }
        await api.post(`/v1/boards/${boardId}/custom-fields`, payload);
      }
      onDone();
    } catch (err: unknown) {
      if (api.isClientError(err) && err.type === 'api') {
        setError(err.message);
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border border-border rounded-md p-3 bg-muted/30 space-y-3"
      aria-label={isEditing ? `Edit field: ${existingField.name}` : 'Add custom field'}
    >
      {/* Field name */}
      <div>
        <label htmlFor="cf-name" className="text-xs text-muted-foreground block mb-1">
          Field Name
        </label>
        <input
          id="cf-name"
          type="text"
          value={form.name}
          onChange={handleNameChange}
          maxLength={MAX_NAME_LENGTH}
          placeholder="Enter field name..."
          className="w-full border border-border rounded-md px-2 py-1 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          autoFocus
        />
        <span className="text-[10px] text-muted-foreground">{form.name.length}/{MAX_NAME_LENGTH}</span>
      </div>

      {/* Field type selector — editable if no cards have values for this field */}
      <div>
        <label className="text-xs text-muted-foreground block mb-1">
          Field Type
        </label>
        <Select
          value={form.fieldType}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger className="w-full h-8 text-sm" aria-label="Select field type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FIELD_TYPES.map((ft) => (
              <SelectItem key={ft.value} value={ft.value}>
                {ft.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isEditing && (
          <span className="text-[10px] text-muted-foreground">Type change is only allowed if no cards have values set for this field.</span>
        )}
      </div>

      {/* Options editor (for dropdown types) */}
      {showOptions && (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">
            Options ({form.options.length}/{MAX_OPTIONS})
          </label>
          <div className="space-y-1 mb-2">
            {form.options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => handleMoveOption(idx, 'up')}
                    disabled={idx === 0}
                    className="text-xs text-muted-foreground hover:text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded disabled:opacity-30 leading-none px-0.5"
                    aria-label={`Move ${opt} up`}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveOption(idx, 'down')}
                    disabled={idx === form.options.length - 1}
                    className="text-xs text-muted-foreground hover:text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded disabled:opacity-30 leading-none px-0.5"
                    aria-label={`Move ${opt} down`}
                  >
                    ▼
                  </button>
                </div>
                <span className="flex-1 text-sm text-card-foreground truncate bg-card border border-border rounded px-2 py-0.5">
                  {opt}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveOption(idx)}
                  className="text-xs text-destructive hover:text-destructive/80 focus:outline-none focus:ring-2 focus:ring-ring rounded px-1"
                  aria-label={`Remove option: ${opt}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-1">
            <input
              type="text"
              value={newOption}
              onChange={(e) => setNewOption(e.target.value.slice(0, MAX_OPTION_LENGTH))}
              onKeyDown={handleOptionKeyDown}
              placeholder="Add option..."
              maxLength={MAX_OPTION_LENGTH}
              className="flex-1 border border-border rounded-md px-2 py-1 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="button"
              onClick={handleAddOption}
              disabled={form.options.length >= MAX_OPTIONS}
              className="text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded-md hover:bg-secondary/80 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Linked-to-list info note */}
      {form.fieldType === 'linked-to-list' && (
        <div className="rounded-md border border-border bg-muted/50 p-2">
          <p className="text-xs text-muted-foreground">
            This field automatically shows which list a card is in. The value updates when cards are moved or lists are renamed. It cannot be edited manually.
          </p>
        </div>
      )}

      {/* Show on card toggle */}
      <div className="flex items-center gap-2">
        <Toggle
          size="sm"
          pressed={form.showOnCard}
          onPressedChange={(pressed) => setForm((prev) => ({ ...prev, showOnCard: pressed }))}
          aria-label="Show on card"
          className="h-7 px-2 text-xs"
        >
          Show on card
        </Toggle>
        <span className="text-[10px] text-muted-foreground">
          {form.showOnCard ? 'Visible on mini-cards' : 'Hidden from mini-cards'}
        </span>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-xs text-destructive" role="alert">{error}</p>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSaving}
          className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Field'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="text-xs text-muted-foreground px-3 py-1.5 hover:text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded-md"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
